import { agencyMemberSeesAllSalons, type AgencyMemberCountryCode } from '@repo/shared/agency';
import { getAgencyMemberName, type AgencyMember } from '../agency-types';

/**
 * Segmented-filter values for the members list. Persisted in the URL as
 * `?filter=` so a deep link (e.g. "Review invites" on the Salons overview)
 * lands with the right segment active and the choice survives refresh.
 */
export const MEMBER_FILTERS = ['all', 'admins', 'members', 'invited'] as const;
export type MemberFilter = (typeof MEMBER_FILTERS)[number];

export const MEMBER_FILTER_LABEL: Record<MemberFilter, string> = {
  all: 'All',
  admins: 'Admins',
  members: 'Members',
  invited: 'Invited',
};

export function isMemberFilter(value: unknown): value is MemberFilter {
  return typeof value === 'string' && (MEMBER_FILTERS as readonly string[]).includes(value);
}

/**
 * Segment predicates. Role segments split everyone by role (disabled people
 * included, so Admins + Members always add up to All and nobody is only
 * reachable through All); Invited is the status cut for pending invites.
 */
const FILTER_PREDICATE: Record<MemberFilter, (member: AgencyMember) => boolean> = {
  all: () => true,
  admins: (member) => member.role === 'ADMIN',
  members: (member) => member.role === 'MEMBER',
  invited: (member) => member.status === 'INVITED',
};

function matchesQuery(member: AgencyMember, query: string): boolean {
  if (!query) return true;
  return `${getAgencyMemberName(member)} ${member.email} ${member.jobTitle ?? ''}`.toLowerCase().includes(query);
}

/** Segment + free-text search (name, email or job title, case-insensitive). Pure; returns a new array. */
export function applyMemberFilter(
  members: AgencyMember[],
  filter: MemberFilter,
  search: string
): AgencyMember[] {
  const query = search.trim().toLowerCase();
  const predicate = FILTER_PREDICATE[filter];
  return members.filter((member) => predicate(member) && matchesQuery(member, query));
}

/** Display order of countries in the members list; members with no country go last. */
const COUNTRY_RANK: Record<AgencyMemberCountryCode, number> = { US: 0, VN: 1, PH: 2, OTHER: 3 };
const NO_COUNTRY_RANK = 4;

function countryRank(member: AgencyMember): number {
  return member.countryCode ? COUNTRY_RANK[member.countryCode] : NO_COUNTRY_RANK;
}

/**
 * List order: people who open every salon (admins and All salons members) first,
 * then by country. Ties keep the API order (newest first). Pure; returns a new array.
 */
export function sortMembersForList(members: AgencyMember[]): AgencyMember[] {
  return [...members].sort(
    (a, b) =>
      Number(agencyMemberSeesAllSalons(b)) - Number(agencyMemberSeesAllSalons(a)) || countryRank(a) - countryRank(b)
  );
}

/** Per-segment counts for the filter pills — computed on the unfiltered list so counts never shrink as you search. */
export function countMemberFilters(members: AgencyMember[]): Record<MemberFilter, number> {
  const counts: Record<MemberFilter, number> = { all: 0, admins: 0, members: 0, invited: 0 };
  for (const member of members) {
    for (const filter of MEMBER_FILTERS) {
      if (FILTER_PREDICATE[filter](member)) counts[filter] += 1;
    }
  }
  return counts;
}
